import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useMutation } from "react-query";
import { AxiosError } from "axios";
import { Button, Dialog, TextInput } from "fe-shared/src/components";
import { CheckIcon, EditIcon } from "fe-shared/src/components/Icons";
import { ApiError } from "fe-shared/src/types/common";
import useDialog from "fe-shared/src/hooks/useDialog";
import * as api from "./modules/api";

interface Props {
  transactionId: number;
  currentTrailerNumber?: string | null;
}

const TrailerNumberEditButton = ({ transactionId, currentTrailerNumber }: Props) => {
  const { t } = useTranslation();
  const [value, setValue] = useState(currentTrailerNumber ?? "");
  const { isDialogRendered, isDialogActive, openDialog, closeDialog } = useDialog();

  const [updateTrailerNumber, { isLoading }] = useMutation<
    unknown,
    AxiosError<ApiError>,
    api.UpdateTrailerNumberParams
  >(api.updateTrailerNumber, {
    throwOnError: true,
    onSuccess: () => {
      document.location.reload();
      closeDialog();
    },
  });

  const isValueValid = value.length <= 7;

  const dialogActions: React.ReactNode[] = [
    <Button
      key="submit"
      id="trailer-number-submit"
      variant="filled"
      iconComponent={CheckIcon}
      onClick={() => isValueValid && updateTrailerNumber({ transactionId, trailerNumber: value })}
      disabled={isLoading || !isValueValid}
    >
      {t("submit")}
    </Button>,
    <Button key="cancel" id="trailer-number-cancel" variant="outlined" onClick={closeDialog}>
      {t("cancel")}
    </Button>,
  ];

  return (
    <>
      <Button
        id="trailer-number-edit-button"
        variant="outlined"
        iconComponent={EditIcon}
        onClick={openDialog}
      >
        {t("transactions.details.trailerNumber.editButton")}
      </Button>
      {isDialogRendered && (
        <Dialog
          modal
          isActive={isDialogActive}
          onClose={closeDialog}
          title={t("transactions.details.trailerNumber.dialogTitle")}
          actions={dialogActions}
        >
          <TextInput
            id="trailer-number-input"
            title={t("transactions.details.trailerNumber.label")}
            placeholder={t("transactions.details.trailerNumber.placeholder")}
            value={value}
            isError={!isValueValid}
            errorText={!isValueValid ? t("validation.maxLength", { max: 7 }) : undefined}
            onChange={(_, v) => setValue(v)}
          />
        </Dialog>
      )}
    </>
  );
};

export default TrailerNumberEditButton;
